/**
 * Registers tool renderers for agent-rendered widgets.
 *
 * Each render tool returns its props as JSON. The registered renderer
 * parses the result and renders the widget inline — no tool-call chrome
 * visible to the user.
 *
 * To add a new widget renderer:
 * 1. Import the component from `./widgets/`.
 * 2. Register it with `useWidgetToolRenderer("tool_name", ...)`.
 */

import { useWidgetToolRenderer } from "@/hooks/useToolRenderer"
import { CountryList } from "./widgets/CountryList"
import { SurveysOverview } from "./widgets/SurveysOverview"
import { ReportWidget } from "./widgets/ReportWidget"
import { FactoidWidget } from "./widgets/FactoidWidget"
import LoadingSpinner from "@/components/shared/LoadingSpinner.tsx"

/**
 * Inline loading placeholder shown while a render tool is streaming its
 * arguments from the model. Gives the user visual feedback that a widget
 * is being prepared.
 *
 * @param label - Short description of what is loading (e.g. "Generating report…").
 * @returns A minimal card with a spinner and label.
 */
function WidgetLoadingSkeleton({ label }: { label: string }) {
  return (
    <div className="my-5">
      <LoadingSpinner loadingContent={label} variant="default" />
    </div>
  )
}

/**
 * Register all widget tool renderers. Call once from ChatInterface.
 */
export function useWidgetRenderers(): void {
  useWidgetToolRenderer("render_country_list", (props) => {
    if (props.status !== "complete" || !props.result) return null
    try {
      const componentProps = JSON.parse(props.result)
      return <CountryList {...componentProps} />
    } catch {
      return null
    }
  })

  useWidgetToolRenderer("render_surveys_overview", (props) => {
    if (props.status !== "complete" || !props.result) return null
    try {
      const componentProps = JSON.parse(props.result)
      return <SurveysOverview {...componentProps} />
    } catch {
      return null
    }
  })

  useWidgetToolRenderer("render_report", (props) => {
    if (props.status === "streaming" || props.status === "executing") {
      return <WidgetLoadingSkeleton label="Generating report…" />
    }
    if (props.status !== "complete" || !props.result) return null
    try {
      const componentProps = JSON.parse(props.result)
      return <ReportWidget {...componentProps} />
    } catch {
      return null
    }
  })

  useWidgetToolRenderer("render_factoid", (props) => {
    if (props.status === "streaming" || props.status === "executing") {
      return <WidgetLoadingSkeleton label="Generating fact-check…" />
    }
    if (props.status !== "complete" || !props.result) return null
    try {
      const componentProps = JSON.parse(props.result)
      return <FactoidWidget {...componentProps} />
    } catch {
      return null
    }
  })
}
